/*
 * themeRuntime.js — framework-agnostic theme preference runtime.
 *
 * Plain ES5 syntax (no const/let/arrow fns) so it transpiles cleanly through
 * Angular's ng-annotate-loader + babel-loader (es2015 preset) AND through
 * Create React App's pipeline without surprises.
 *
 * Imported by both the Angular shell and the standalone React app. In embedded
 * mode the Angular shell calls initTheme() at bootstrap; React mounts inherit
 * the data-eci-theme attribute from <html>.
 */

var STORAGE_KEY = 'eci-appearance-theme';
var CHANGE_EVENT = 'eci-theme-change';

function getPreference() {
  try {
    var v = window.localStorage.getItem(STORAGE_KEY);
    if (v === 'light' || v === 'dark' || v === 'system') return v;
  } catch (e) {}
  // Default to 'system' — follow the OS appearance setting. applyTheme
  // now resolves 'system' to the effective 'light'/'dark' and writes the
  // attribute, so all html[data-eci-theme="dark"] overrides match in
  // system+OS-dark identically to explicit dark.
  return 'system';
}

function setPreference(mode) {
  if (mode !== 'light' && mode !== 'dark' && mode !== 'system') return;
  try { window.localStorage.setItem(STORAGE_KEY, mode); } catch (e) {}
  applyTheme(mode);
}

function getEffectiveMode() {
  var pref = getPreference();
  if (pref === 'light' || pref === 'dark') return pref;
  return systemPrefersDark() ? 'dark' : 'light';
}

function systemPrefersDark() {
  try {
    return window.matchMedia && window.matchMedia('(prefers-color-scheme: dark)').matches;
  } catch (e) { return false; }
}

function applyTheme(mode) {
  var root = document.documentElement;
  // Resolve 'system' to the effective light/dark so the attribute always
  // reflects what's actually on screen. This lets every CSS rule keyed on
  // html[data-eci-theme="dark"] match in both explicit-dark and
  // system+OS-dark — otherwise system mode would render with the CSS
  // variables flipped (via @media) but skip the per-component dark
  // overrides, producing a "third theme" that's neither light nor dark.
  var effective = mode === 'light' || mode === 'dark'
    ? mode
    : (systemPrefersDark() ? 'dark' : 'light');
  root.setAttribute('data-eci-theme', effective);

  // Swap the favicon to match the effective theme. Browser tab strips are
  // commonly dark, so the default favicon disappears when the system is in
  // dark mode; the reversed full-color badge (which is square, so it
  // doesn't stretch in the favicon slot) reads correctly.
  try {
    var link = document.getElementById('eci-favicon');
    if (link) {
      link.setAttribute('href', effective === 'dark'
        ? '/images/favicon-dark.png'
        : '/favicon.ico');
    }
  } catch (e) {}

  try {
    var detail = { mode: mode, effective: effective };
    window.dispatchEvent(new CustomEvent(CHANGE_EVENT, { detail: detail }));
  } catch (e) {}
}

function initTheme() {
  applyTheme(getPreference());

  // Cross-bundle dedup: Angular's bundle and React's bundle each have their
  // own copy of this module, so a module-local flag wouldn't catch the second
  // initTheme(). Use a window property — the first caller wins, subsequent
  // calls only re-apply the stored preference and skip listener registration.
  if (window.__eciThemeRuntimeListenersAttached) return;
  window.__eciThemeRuntimeListenersAttached = true;

  try {
    var mql = window.matchMedia('(prefers-color-scheme: dark)');
    var onMqlChange = function () {
      if (getPreference() === 'system') applyTheme('system');
    };
    if (mql.addEventListener) mql.addEventListener('change', onMqlChange);
    else if (mql.addListener) mql.addListener(onMqlChange);
  } catch (e) {}

  try {
    window.addEventListener('storage', function (ev) {
      if (ev && ev.key === STORAGE_KEY) applyTheme(getPreference());
    });
  } catch (e) {}
}

module.exports = {
  STORAGE_KEY: STORAGE_KEY,
  CHANGE_EVENT: CHANGE_EVENT,
  getPreference: getPreference,
  setPreference: setPreference,
  getEffectiveMode: getEffectiveMode,
  applyTheme: applyTheme,
  initTheme: initTheme
};
